/**
 * Centralized helper functions for publish status mapping and display
 */

/**
 * Display-level publish steps in order. These are the user-facing phase
 * names shown in the publish status UI. The naming is intentionally
 * product-driven, not technical:
 *
 *   - "preparing"   - CMS is building the bundle.
 *   - "connecting"  - CMS is handing the bundle off to Echo / the kiosk.
 *   - "sending"     - Bundle is being transferred to the kiosk. Despite the
 *                     active-voice name, this phase is actually written by
 *                     the kiosk while it DOWNLOADS the bundle from S3. The
 *                     product owner chose "sending" because end users (CMS
 *                     authors) think of the CMS as pushing content out, not
 *                     the kiosk pulling it in. Sub-steps like
 *                     "downloading-bundle" come from the kiosk side and are
 *                     mapped to this group. "downloading-bundle" is treated
 *                     as covering the whole download phase including
 *                     initiation, so a stall after Echo is notified is
 *                     reported here rather than as a separate "connecting"
 *                     failure.
 *   - "installing"  - Kiosk is unzipping/installing the bundle.
 *   - "restarting"  - Kiosk is restarting into the new bundle.
 *   - "complete"    - Done.
 */
export const STATUS_STEPS = [
  "preparing",
  "connecting",
  "sending",
  "installing",
  "restarting",
  "complete"
];

// Map detailed status types to display status types for progress grouping
// This allows multiple detailed statuses to be grouped under a single display status
export const STATUS_GROUP_MAP = {
  "bundle-record-created": "preparing",
  "bundle-created": "preparing",
  "uploading-bundle": "preparing",
  "bundle-uploaded": "preparing",
  "revert-initiated": "preparing",
  "notifying-echo": "connecting",
  "echo-notified": "connecting",
  "downloading-bundle": "sending",
  "bundle-downloaded": "sending",
  "installing-bundle": "installing",
  "installing-complete": "installing"
};

// Map display status to human-readable step label
export const STATUS_STEP_LABELS = {
  "preparing": "Preparing content",
  "connecting": "Connecting to kiosk",
  "sending": "Sending content",
  "installing": "Installing content",
  "restarting": "Restarting kiosk",
  "complete": "Done"
};

/**
 * Maps a detailed status to its display status group
 * @param {String} status - The detailed status from the backend
 * @returns {String} The display status for the progress stepper
 */
export function getDisplayStatus(status) {
  if (!status) return null;
  
  // If status is in the group map, return the mapped value
  if (STATUS_GROUP_MAP[status]) {
    return STATUS_GROUP_MAP[status];
  }
  
  // Otherwise, return the status as-is (for statuses like "connecting", "complete", etc.)
  return status;
}

/**
 * Gets the step label for a display status
 * @param {String} displayStatus - The display status (e.g., "preparing", "connecting")
 * @returns {String} The human-readable step label
 */
export function getStepLabel(displayStatus) {
  return STATUS_STEP_LABELS[displayStatus] || displayStatus;
}

/**
 * Gets the status label for display in history tables
 * Maps the final status to a display status and returns the step label
 * @param {String} status - The final status from the publish history
 * @returns {String} The status label to display (e.g., "Preparing content", "Connecting to kiosk")
 */
export function getStatusLabelForHistory(status) {
  if (!status) return "";
  
  const displayStatus = getDisplayStatus(status);
  if (!displayStatus) return "";
  
  // Return the step label for the display status
  return getStepLabel(displayStatus);
}

